import Divider from '@mui/material/Divider';
import { useEffect, useState } from 'react';

import { Typography } from 'design_system/src';
import { getLead } from 'models/lead/controllers/lead.controller';
import {
	Lead,
	LeadSku,
	LeadSkuVariant,
} from 'models/lead/interfaces/lead.interface';
import { formatDate, formatHour } from 'models/shared/helpers/dates';
import { priceFormatFinancial } from 'models/shared/helpers/prices';
import motoLoading from 'static/icons/loading_moto.gif';
import { Grid } from '@mui/material';
import {
	LeadStatus,
	LeadStep,
	LeadStepValues,
} from 'models/lead/enums/lead.enums';
import { vehicleColorValues } from 'models/inventory/enums/vehicle.enum';

interface LeadDetailsProps {
	leadId: string;
}

interface FieldRowProps {
	label: string;
	value?: string | number | null;
}

function FieldRow({ label, value }: FieldRowProps) {
	return (
		<div className="m_b_sm">
			<Typography scale="xsmall" weight="400" textColor="neutral_600">
				{label}
			</Typography>
			<Typography scale="medium" weight="600" textColor="neutral_900">
				{value === undefined || value === null || value === '' ? '—' : value}
			</Typography>
		</div>
	);
}

function SectionTitle({
	children,
	current,
}: {
	children: string;
	current?: boolean;
}) {
	return (
		<div className="display_flex flex_justify_between">
			<Typography
				scale="large"
				weight="600"
				textColor="neutral_700"
				className="m_b_md m_t_lg"
			>
				{children}
			</Typography>
			{current && (
				<Typography
					scale="small"
					weight="600"
					textColor="green_300"
					className="m_b_md m_t_lg"
				>
					Paso actual
				</Typography>
			)}
		</div>
	);
}

function SectionSubtitle({ children }: { children: string }) {
	return (
		<Typography
			scale="medium"
			weight="600"
			textColor="neutral_600"
			className="m_b_md"
		>
			{children}
		</Typography>
	);
}

function LeadDetails({ leadId }: LeadDetailsProps) {
	const [lead, setLead] = useState<Lead | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		setLoading(true);
		getLead(leadId)
			.then((result) => setLead(result))
			.finally(() => setLoading(false));
	}, [leadId]);

	if (loading) {
		return (
			<div className="dim_100_per flex_center_col">
				<Typography
					weight="600"
					scale="heading4"
					textColor="neutral_800"
					className="m_b_xs"
				>
					Cargando lead...
				</Typography>
				<img src={motoLoading} alt="" className="h_xxxl" />
			</div>
		);
	}

	if (!lead) {
		return (
			<div className="dim_100_per flex_center_col">
				<Typography weight="600" scale="heading4" textColor="neutral_800">
					No se encontró el lead
				</Typography>
			</div>
		);
	}

	const address = lead.personalInformation?.fullAddress;
	const sku = typeof lead.sku === 'object' ? (lead.sku as LeadSku) : undefined;
	const skuVariant =
		typeof lead.skuVariant === 'object'
			? (lead.skuVariant as LeadSkuVariant)
			: undefined;
	const hasSelectedSku = !!lead.sku;
	const skuModelLabel = [sku?.brandName, sku?.modelName]
		.filter(Boolean)
		.join(' ');
	const skuLabel = sku
		? [sku.code, sku.description || skuModelLabel].filter(Boolean).join(' - ')
		: undefined;
	const skuVariantColorLabel = skuVariant?.color
		? vehicleColorValues[skuVariant.color as keyof typeof vehicleColorValues] ??
		  skuVariant.color
		: undefined;
	const skuVariantLabel = skuVariant
		? [priceFormatFinancial(skuVariant.price, 0), skuVariantColorLabel]
				.filter(Boolean)
				.join(' · ')
		: undefined;

	return (
		<div className="p_x_sm p_y_lg">
			<div className="m_b_lg">
				<Typography scale="heading4" weight="600" textColor="neutral_900">
					Lead {lead._id}
				</Typography>
				<Typography scale="medium" weight="400" textColor="neutral_600">
					Creado el {formatDate(lead.createdAt)} {formatHour(lead.createdAt)}
				</Typography>
				<Typography scale="medium" weight="400" textColor="neutral_600">
					Actualizado el {formatDate(lead.updatedAt)}{' '}
					{formatHour(lead.updatedAt)}
				</Typography>
			</div>
			<FieldRow label="Correo" value={lead.email} />

			<Divider />

			<SectionTitle current={lead.step === LeadStep.personalInformationStep}>
				{LeadStepValues[LeadStep.personalInformationStep]}
			</SectionTitle>
			<Grid container>
				<Grid xs={6}>
					<FieldRow label="Primer nombre" value={lead.firstName} />
					<FieldRow label="Primer apellido" value={lead.lastName} />
				</Grid>
				<Grid xs={6}>
					<FieldRow label="Segundo nombre" value={lead.middleName} />
					<FieldRow label="Segundo apellido" value={lead.secondLastName} />
				</Grid>
			</Grid>
			<FieldRow label="CURP" value={lead.curp} />

			<Divider className="m_t_md" />
			<SectionTitle current={lead.step === LeadStep.addressStep}>
				{LeadStepValues[LeadStep.addressStep]}
			</SectionTitle>
			<Grid container>
				<Grid xs={6}>
					<FieldRow label="Código postal" value={address?.zipCode ?? '-'} />
					<FieldRow label="Colonia" value={address?.neighborhood ?? '-'} />
					<FieldRow
						label="Calle y número"
						value={[
							address?.street ?? '-',
							address?.extNumber ?? '-',
							address?.intNumber ?? '-',
						]
							.filter(Boolean)
							.join(' ')}
					/>
				</Grid>
				<Grid xs={6}>
					<FieldRow
						label="Estado"
						value={[address?.state ?? '-', address?.stateCode ?? '-']
							.filter(Boolean)
							.join(' · ')}
					/>
					<FieldRow
						label="Delegación/Municipio"
						value={address?.delegation ?? '-'}
					/>
				</Grid>
			</Grid>

			<Divider className="m_t_md" />
			<SectionTitle current={lead.step === LeadStep.phoneStep}>
				{LeadStepValues[LeadStep.phoneStep]}
			</SectionTitle>
			<Grid container>
				<Grid xs={6}>
					<FieldRow
						label="Código"
						value={lead.personalInformation?.dialCode ?? '-'}
					/>
				</Grid>
				<Grid xs={6}>
					<FieldRow
						label="Teléfono"
						value={lead.personalInformation?.phone ?? '-'}
					/>
				</Grid>
			</Grid>

			<Divider className="m_t_md" />
			<SectionTitle current={lead.step === LeadStep.phoneValidationStep}>
				{LeadStepValues[LeadStep.phoneValidationStep]}
			</SectionTitle>
			<Grid container>
				<Grid xs={6}>
					<FieldRow
						label="Verificado el"
						value={
							lead.sofiaOtpVerification?.verifiedAt
								? `${formatDate(
										lead.sofiaOtpVerification?.verifiedAt,
								  )} ${formatHour(lead.sofiaOtpVerification?.verifiedAt)}`
								: '-'
						}
					/>
				</Grid>
				<Grid xs={6}>
					<FieldRow
						label="Intentos restantes"
						value={lead.sofiaOtpVerification?.attempts ?? '-'}
					/>
				</Grid>
			</Grid>

			<Divider className="m_t_md" />
			<SectionTitle
				current={
					lead.step === LeadStep.financialInformationStep &&
					lead.status !== LeadStatus.completed
				}
			>
				{LeadStepValues[LeadStep.financialInformationStep]}
			</SectionTitle>
			<FieldRow
				label="Ingreso mensual"
				value={
					lead.financialInformation?.monthlyIncome !== undefined
						? priceFormatFinancial(lead.financialInformation.monthlyIncome, 0)
						: undefined
				}
			/>
			<FieldRow
				label="Dependientes económicos"
				value={lead.personalInformation?.dependantsCount}
			/>

			<Divider className="m_t_md" />
			<SectionTitle>Otros</SectionTitle>

			<SectionSubtitle>SKU seleccionado</SectionSubtitle>
			<Grid container>
				<Grid xs={6}>
					<FieldRow
						label="¿Seleccionó SKU?"
						value={hasSelectedSku ? 'Sí' : 'No'}
					/>
					<FieldRow label="SKU" value={skuLabel} />
				</Grid>
				<Grid xs={6}>
					<FieldRow label="Variante" value={skuVariantLabel} />
				</Grid>
			</Grid>

			<Divider className="m_y_md" />
			<SectionSubtitle>Utm</SectionSubtitle>
			<Grid container>
				<Grid xs={6}>
					<FieldRow label="Campaña" value={lead.utm?.utmCampaign} />
					<FieldRow label="Medio" value={lead.utm?.utmMedium} />
					<FieldRow label="Contenido" value={lead.utm?.utmContent} />
				</Grid>
				<Grid xs={6}>
					<FieldRow label="Fuente" value={lead.utm?.utmSource} />
					<FieldRow label="Término" value={lead.utm?.utmTerm} />
				</Grid>
			</Grid>

			<Divider className="m_y_md" />
			<SectionSubtitle>
				Politica de privacidad y Términos y condiciones
			</SectionSubtitle>
			<Grid container>
				<Grid xs={6}>
					<FieldRow
						label="Aceptación de política de privacidad"
						value={
							lead.privacyPolicyAcceptance?.acceptedAt
								? `${formatDate(
										lead.privacyPolicyAcceptance?.acceptedAt,
								  )} ${formatHour(lead.privacyPolicyAcceptance?.acceptedAt)}`
								: '-'
						}
					/>
					<FieldRow
						label="Versión de política de privacidad"
						value={lead.privacyPolicyAcceptance?.privacyPolicyVersion}
					/>
					<FieldRow
						label="Método de aceptación de política de privacidad"
						value={lead.privacyPolicyAcceptance?.acceptanceMethod}
					/>
				</Grid>
				<Grid xs={6}>
					<FieldRow
						label="Aceptación de términos y condiciones"
						value={
							lead.termsOfServiceAcceptance?.acceptedAt
								? `${formatDate(
										lead.termsOfServiceAcceptance?.acceptedAt,
								  )} ${formatHour(lead.termsOfServiceAcceptance?.acceptedAt)}`
								: '-'
						}
					/>
					<FieldRow
						label="Versión de términos y condiciones"
						value={lead.termsOfServiceAcceptance?.termsVersion}
					/>
					<FieldRow
						label="Método de términos y condiciones"
						value={lead.termsOfServiceAcceptance?.acceptanceMethod}
					/>
				</Grid>
			</Grid>
		</div>
	);
}

export default LeadDetails;
